import { GetCurrenciesReturn, GetRatesBatchReturn } from '@web/walter-lib/api'
import { formatNumberString } from '@web/walter-ui-kit'
import { useMemo } from 'react'
import { useBaseCurrency } from './useBaseCurrency'
import { usePrevCurrentRatesBatch } from './usePrevCurrentRatesBatch'
import { useCommonCMSConfig, useCurrencies } from '~api'
import { getPrecision } from '~utils/getPrecision'

export type RatesDataMap = Partial<Record<string, { price: string; changePercentage?: string; chart?: number[] }>>

interface GetRatesDataMapProps {
    isNewRateService: boolean
    ratesData: GetRatesBatchReturn | undefined
    prevRatesData: GetRatesBatchReturn | undefined
    withChart?: boolean
    currencies?: GetCurrenciesReturn
}

export const useRatesDataMap = ({
    isNewRateService,
    ratesData,
    withChart,
    currencies,
    prevRatesData,
}: GetRatesDataMapProps): RatesDataMap =>
    useMemo(() => {
        if (!isNewRateService) {
            return {}
        }

        const coinData: RatesDataMap = {}

        ratesData?.[0]?.prices?.forEach(({ base, price }) => {
            if (!base) {
                return
            }

            const formattedPrice = formatNumberString({
                num: price ?? '0',
                precision: getPrecision(currencies?.[base]?.precision_transfer),
                formatToZero: false,
            })

            coinData[base] = {
                price: formattedPrice,
                changePercentage: undefined,
                chart: undefined,
            }
        })

        prevRatesData?.[0]?.prices?.forEach(({ base, price }) => {
            if (!base) {
                return
            }

            const prevFormattedPrice = Number(
                formatNumberString({
                    num: price ?? '0',
                    precision: getPrecision(currencies?.[base]?.precision_transfer),
                    formatToZero: false,
                })
            )

            const formattedPrice = Number(coinData[base]?.price)

            const difference = formattedPrice - prevFormattedPrice
            const changePercentage = difference
                ? formatNumberString({
                      num: (difference / prevFormattedPrice) * 100,
                      precision: 2,
                  })
                : '0'

            let chart

            if (withChart) {
                chart = [prevFormattedPrice, formattedPrice].map(value =>
                    Number(
                        formatNumberString({
                            num: value || '',
                            precision: getPrecision(currencies?.[base]?.precision_transfer),
                            formatToZero: false,
                        })
                    )
                )
            }

            coinData[base] = {
                price: coinData[base]?.price ?? '0',
                chart,
                changePercentage,
            }
        })

        return coinData
    }, [currencies, isNewRateService, prevRatesData, ratesData, withChart])

interface Props {
    baseCurrencies: string[]
    quoteCurrency?: string
}

export const useGetRatesMap = (props?: Props) => {
    const { data: cmsConfig, isValidating: isCmsConfigLoading } = useCommonCMSConfig()
    const { data: currencies, isValidating: isCurrenciesLoading } = useCurrencies()
    const { calculatedBaseCurrency } = useBaseCurrency()

    const baseCurrencies = props?.baseCurrencies ?? Object.keys(currencies ?? {})
    const isNewRateService = cmsConfig?.features?.isNewRateService

    const {
        ratesData,
        prevRatesData,
        isLoading: isRatesLoading,
    } = usePrevCurrentRatesBatch({
        isNewRateService,
        currencies: baseCurrencies,
        quote: props?.quoteCurrency || calculatedBaseCurrency,
    })

    const ratesDataMap = useRatesDataMap({
        ratesData,
        currencies,
        prevRatesData,
        isNewRateService: Boolean(isNewRateService),
    })

    return {
        data: ratesDataMap,
        isLoading: isCmsConfigLoading || isCurrenciesLoading || isRatesLoading,
        isNewRateService: Boolean(isNewRateService),
    }
}
